import React, { useState, useEffect, useMemo } from 'react';
import superagent from 'superagent';
import { Bar } from 'react-chartjs-2';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { Download } from 'lucide-react';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from "@shadcn/components/ui/card";
import { Button } from "@shadcn/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@shadcn/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shadcn/components/ui/tabs";



import { backendUrl } from "../../../helpers/consts";
import TopBottomHeader from "./components/TopBottomHeader";
import TopBottomTable from "./components/TopBottomTable";
import StateAnalysisSection from "./components/StateAnalysisSection";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register required Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const TopBottomReport = () => {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [stateData, setStateData] = useState(null);
  const [activeTab, setActiveTab] = useState('AU');
  const [exportLoading, setExportLoading] = useState(false);
  
  
  const [dateRange, setDateRange] = useState({
    startDate: startOfMonth(new Date()),
    endDate: new Date()
  });

  const fetchStateData = async (customDateRange = dateRange) => {
    try {
      const { startDate, endDate } = customDateRange;
      
  
      const validStartDate = startDate instanceof Date && !isNaN(startDate) ? startDate : startOfMonth(new Date());
      const validEndDate = endDate instanceof Date && !isNaN(endDate) ? endDate : new Date();
      
      const formattedStartDate = format(validStartDate, 'yyyy-MM-dd');
      const formattedEndDate = format(validEndDate, 'yyyy-MM-dd');
      
      const response = await superagent
        .get(`${backendUrl}/reports/top-bottom-by-state`)
        .query({ 
          startDate: formattedStartDate,
          endDate: formattedEndDate 
        })
        .set('Authorization', `Bearer ${localStorage.getItem("authJWT")}`)
        .set('Accept', 'application/json');
      
      setStateData(response.body);
    } catch (err) {
      console.error('Error fetching state data:', err);
      toast.error('Failed to load state data');
    }
  };

  const fetchData = async (customDateRange = dateRange) => {
    try {
      setLoading(true);
      
      const { startDate, endDate } = customDateRange;
      
      
      const validStartDate = startDate instanceof Date && !isNaN(startDate) ? startDate : startOfMonth(new Date());
      const validEndDate = endDate instanceof Date && !isNaN(endDate) ? endDate : new Date();
      
      const formattedStartDate = format(validStartDate, 'yyyy-MM-dd');
      const formattedEndDate = format(validEndDate, 'yyyy-MM-dd');
      
      
      const [countryResponse] = await Promise.all([
        superagent
          .post(`${backendUrl}/reports/mtd-sales`)
          .send({ 
            startDate: formattedStartDate,
            endDate: formattedEndDate 
          })
          .set('Authorization', `Bearer ${localStorage.getItem("authJWT")}`)
          .set('Accept', 'application/json'),
        fetchStateData(customDateRange)
      ]);
      
      
      const rawData = countryResponse.body.rawData || {};
      const auChannels = countryResponse.body.auChannels || {};
      const processedData = processReportData(rawData, auChannels);
      setReportData(processedData);
    } catch (err) {
      console.error('Error fetching top bottom report data:', err);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  
  const processReportData = (rawData, auChannels) => {
    const regions = ['AU', 'US', 'UK'];
    const result = {
      top10: {},
      bottom10: {},
      auChannels: {
        onlineTop10: [],
        retailTop10: []
      }
    };

    
    regions.forEach(region => {
    
      const regionProducts = rawData[region] || [];
      
    
      const sortedByMTD = [...regionProducts].sort((a, b) => b.mtd - a.mtd);
      
    
      result.top10[region] = sortedByMTD.slice(0, 10);
      result.bottom10[region] = sortedByMTD.slice(-10).reverse();
    });

    
    if(auChannels){
      console.log("enter here", auChannels)
      result.auChannels.onlineTop10 = auChannels.onlineTop10
      result.auChannels.retailTop10 = auChannels.retailTop10
    }

    

    return result;
  };
  
  const generateTopMTDChartData = (products) => {
    if (!products || products.length === 0) return null;

    
    const chartProducts = [...products].reverse();
    
    return {
      labels: chartProducts?.map(item => `${item.productName} - ${item.color}`) || [],
      datasets: [
        {
          label: 'Sales',
          data: chartProducts.map(item => item.mtd),
          backgroundColor: 'rgba(53, 162, 235, 0.8)',
          borderColor: 'rgba(53, 162, 235, 1)',
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  };

  
  const generateBottomChartData = (products) => {
    if (!products || products.length === 0) return null;

    return {
      labels: products?.map(item => `${item.productName} - ${item.color}`) || []  ,
      datasets: [
        {
          label: 'Sales',
          data: products.map(item => item.mtd),
          backgroundColor: 'rgba(255, 99, 132, 0.8)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  };

  
  const horizontalBarOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            family: "'Inter', sans-serif",
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1e293b',
        bodyColor: '#475569',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        titleFont: {
          family: "'Inter', sans-serif",
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          family: "'Inter', sans-serif",
          size: 12
        },
        callbacks: {
          label: function(context) {
            return `Sales: ${context.parsed.x}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          borderDash: [2, 4],
          color: 'rgba(148, 163, 184, 0.2)'
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif"
          }
        }
      },
      y: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif"
          }
        }
      }
    },
    animation: {
      duration: 1000,
      easing: 'easeOutQuart'
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getStockToMonthLevelColor = (stockLevel) => {
    if (stockLevel < 0) return 'text-red-600';
    if (stockLevel < 10) return 'text-orange-600';
    if (stockLevel < 50) return 'text-yellow-600';
    return 'text-green-600';
  };

  const handleDateRangeChange = (newDateRange) => {
    if (newDateRange.startDate && newDateRange.endDate) {
      setDateRange(newDateRange);
      fetchData(newDateRange);
    }
  };

  
  const exportCountryData = () => {
    if (!reportData) {
      toast.error('No data available to export');
      return;
    }

    setExportLoading(true);
    
    try {
      const workbook = XLSX.utils.book_new();
      
  
      if (reportData.top10.AU || reportData.bottom10.AU) {
        const auWorksheet = XLSX.utils.json_to_sheet([]);
        
  
        if (reportData.top10.AU?.length > 0) {
          XLSX.utils.sheet_add_aoa(auWorksheet, [
            ['Top 10 AU Products'],
            ['SKU', 'Product Name', 'Color', 'Sales', 'SOH']
          ], {origin: 'A1'});
          
          const topData = reportData.top10.AU.map(item => ([
            item.sku, item.productName, item.color, item.mtd, item.soh
          ]));
          XLSX.utils.sheet_add_aoa(auWorksheet, topData, {origin: 'A3'});
        }
        
        
        if (reportData.bottom10.AU?.length > 0) {
          const bottomStartRow = 3 + (reportData.top10.AU?.length || 0) + 2;
          XLSX.utils.sheet_add_aoa(auWorksheet, [
            ['Bottom 10 AU Products'],
            ['SKU', 'Product Name', 'Color', 'Sales', 'SOH']
          ], {origin: `A${bottomStartRow}`});
          
          const bottomData = reportData.bottom10.AU.map(item => ([
            item.sku, item.productName, item.color, item.mtd, item.soh
          ]));
          XLSX.utils.sheet_add_aoa(auWorksheet, bottomData, {origin: `A${bottomStartRow + 2}`});
        }
        
        
        if (reportData.auChannels?.onlineTop10?.length > 0) {
          const onlineStartRow = 3 + (reportData.top10.AU?.length || 0) + 2 + (reportData.bottom10.AU?.length || 0) + 3;
          XLSX.utils.sheet_add_aoa(auWorksheet, [
            ['AU Online Top 10'],
            ['SKU', 'Product Name', 'Color', 'Sales', 'SOH']
          ], {origin: `A${onlineStartRow}`});
          
          const onlineData = reportData.auChannels.onlineTop10.map(item => ([
            item.sku, item.productName, item.color, item.mtd, item.soh
          ]));
          XLSX.utils.sheet_add_aoa(auWorksheet, onlineData, {origin: `A${onlineStartRow + 2}`});
        }
        
        
        if (reportData.auChannels?.retailTop10?.length > 0) {
          const retailStartRow = 3 + (reportData.top10.AU?.length || 0) + 2 + (reportData.bottom10.AU?.length || 0) + 3 + (reportData.auChannels?.onlineTop10?.length || 0) + 3;
          XLSX.utils.sheet_add_aoa(auWorksheet, [
            ['AU Retail Top 10'],
            ['SKU', 'Product Name', 'Color', 'Sales', 'SOH']
          ], {origin: `A${retailStartRow}`});
          
          const retailData = reportData.auChannels.retailTop10.map(item => ([
            item.sku, item.productName, item.color, item.mtd, item.soh
          ]));
          XLSX.utils.sheet_add_aoa(auWorksheet, retailData, {origin: `A${retailStartRow + 2}`});
        }
        
        // Set column widths for AU sheet
        auWorksheet['!cols'] = [
          {width: 15}, // SKU
          {width: 30}, // Product Name
          {width: 15}, // Color
          {width: 12}, // Sales
          {width: 10}  // SOH
        ];
        
        XLSX.utils.book_append_sheet(workbook, auWorksheet, 'AU');
      }
      
      
      if (reportData.top10.US || reportData.bottom10.US) {
        const usWorksheet = XLSX.utils.json_to_sheet([]);
        
        // Top 10 US Products
        if (reportData.top10.US?.length > 0) {
          XLSX.utils.sheet_add_aoa(usWorksheet, [
            ['Top 10 US Products'],
            ['SKU', 'Product Name', 'Color', 'Sales', 'SOH']
          ], {origin: 'A1'});
          
          const topData = reportData.top10.US.map(item => ([
            item.sku, item.productName, item.color, item.mtd, item.soh
          ]));
          XLSX.utils.sheet_add_aoa(usWorksheet, topData, {origin: 'A3'});
        }
        
        
        if (reportData.bottom10.US?.length > 0) {
          const bottomStartRow = 3 + (reportData.top10.US?.length || 0) + 2;
          XLSX.utils.sheet_add_aoa(usWorksheet, [
            ['Bottom 10 US Products'],
            ['SKU', 'Product Name', 'Color', 'Sales', 'SOH']
          ], {origin: `A${bottomStartRow}`});
          
          const bottomData = reportData.bottom10.US.map(item => ([
            item.sku, item.productName, item.color, item.mtd, item.soh
          ]));
          XLSX.utils.sheet_add_aoa(usWorksheet, bottomData, {origin: `A${bottomStartRow + 2}`});
        }
        
        // Set column widths for US sheet
        usWorksheet['!cols'] = [
          {width: 15}, // SKU
          {width: 30}, // Product Name
          {width: 15}, // Color
          {width: 12}, // Sales
          {width: 10}  // SOH
        ];
        
        XLSX.utils.book_append_sheet(workbook, usWorksheet, 'US');
      }
      
      // Export UK data
      if (reportData.top10.UK || reportData.bottom10.UK) {
        const ukWorksheet = XLSX.utils.json_to_sheet([]);
        
        // Top 10 UK Products
        if (reportData.top10.UK?.length > 0) {
          XLSX.utils.sheet_add_aoa(ukWorksheet, [
            ['Top 10 UK Products'],
            ['SKU', 'Product Name', 'Color', 'Sales', 'SOH']
          ], {origin: 'A1'});
          
          const topData = reportData.top10.UK.map(item => ([
            item.sku, item.productName, item.color, item.mtd, item.soh
          ]));
          XLSX.utils.sheet_add_aoa(ukWorksheet, topData, {origin: 'A3'});
        }
        
        // Bottom 10 UK Products
        if (reportData.bottom10.UK?.length > 0) {
          const bottomStartRow = 3 + (reportData.top10.UK?.length || 0) + 2;
          XLSX.utils.sheet_add_aoa(ukWorksheet, [
            ['Bottom 10 UK Products'],
            ['SKU', 'Product Name', 'Color', 'Sales', 'SOH']
          ], {origin: `A${bottomStartRow}`});
          
          const bottomData = reportData.bottom10.UK.map(item => ([
            item.sku, item.productName, item.color, item.mtd, item.soh
          ]));
          XLSX.utils.sheet_add_aoa(ukWorksheet, bottomData, {origin: `A${bottomStartRow + 2}`});
        }
        
        // Set column widths for UK sheet
        ukWorksheet['!cols'] = [
          {width: 15}, // SKU
          {width: 30}, // Product Name
          {width: 15}, // Color
          {width: 12}, // Sales
          {width: 10}  // SOH
        ];
        
        XLSX.utils.book_append_sheet(workbook, ukWorksheet, 'UK');
      }
      
      // Generate filename with date range
      const startDate = format(dateRange.startDate, 'yyyy-MM-dd');
      const endDate = format(dateRange.endDate, 'yyyy-MM-dd');
      const filename = `top-bottom-sales_${startDate}_to_${endDate}.xlsx`;
      
      // Save file
      XLSX.writeFile(workbook, filename);
      toast.success('Export completed successfully');
      
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  // Export Top & Bottom Sales by State
  const exportStateData = () => {
    if (!stateData || stateData.length === 0) {
      toast.error('No state data available to export');
      return;
    }

    setExportLoading(true);
    
    try {
      const workbook = XLSX.utils.book_new();
      
      stateData.forEach(stateInfo => {
        const worksheet = XLSX.utils.json_to_sheet([]);
        
        // Top 10 Products for this state
        if (stateInfo.topProducts?.length > 0) {
          XLSX.utils.sheet_add_aoa(worksheet, [
            [`Top 10 Products - ${stateInfo.state}`],
            ['SKU', 'Product Name', 'Color', 'Total Qty', 'Total Orders', 'Retail', 'Online']
          ], {origin: 'A1'});
          
          const topData = stateInfo.topProducts.map(product => ([
            product.sku, product.productName, product.color, 
            product.totalQuantity, product.totalOrders, product.totalRetail, product.totalOnline
          ]));
          XLSX.utils.sheet_add_aoa(worksheet, topData, {origin: 'A3'});
        }
        
        // Bottom 10 Products for this state
        if (stateInfo.bottomProducts?.length > 0) {
          const bottomStartRow = 3 + (stateInfo.topProducts?.length || 0) + 2;
          XLSX.utils.sheet_add_aoa(worksheet, [
            [`Bottom 10 Products - ${stateInfo.state}`],
            ['SKU', 'Product Name', 'Color', 'Total Qty', 'Total Orders', 'Retail', 'Online']
          ], {origin: `A${bottomStartRow}`});
          
          const bottomData = stateInfo.bottomProducts.map(product => ([
            product.sku, product.productName, product.color, 
            product.totalQuantity, product.totalOrders, product.totalRetail, product.totalOnline
          ]));
          XLSX.utils.sheet_add_aoa(worksheet, bottomData, {origin: `A${bottomStartRow + 2}`});
        }
        
        // Set column widths for state sheet
        worksheet['!cols'] = [
          {width: 15}, // SKU
          {width: 30}, // Product Name
          {width: 15}, // Color
          {width: 12}, // Total Qty
          {width: 12}, // Total Orders
          {width: 10}, // Retail
          {width: 10}  // Online
        ];
        
        // Clean sheet name (Excel sheet names have character limits)
        const sheetName = stateInfo.state.replace(/[^a-zA-Z0-9]/g, '').substring(0, 31);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      });
      
      // Generate filename with date range
      const startDate = format(dateRange.startDate, 'yyyy-MM-dd');
      const endDate = format(dateRange.endDate, 'yyyy-MM-dd');
      const filename = `top-bottom-sales-by-state_${startDate}_to_${endDate}.xlsx`;
      
      // Save file
      XLSX.writeFile(workbook, filename);
      toast.success('Export completed successfully');
      
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export state data');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="page-content">
      <div className="container mx-auto px-4 lg:max-w-[85%]">
        
        
        <Card className="mb-6 overflow-hidden">
          <CardContent className="p-6">
            <TopBottomHeader 
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
            />
            
            {/* Date Range Display */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Date Range:</h4>
              <p className="text-sm text-gray-600">
                {format(dateRange.startDate, "dd/MM/yyyy")} to {format(dateRange.endDate, "dd/MM/yyyy")}
                <span className="ml-2 text-xs text-gray-500">
                  ({format(dateRange.startDate, "dd MMM yyyy")} - {format(dateRange.endDate, "dd MMM yyyy")})
                </span>
              </p>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex justify-center items-center h-[400px]">
                <div className="flex flex-col items-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                  <span className="mt-2 text-sm text-gray-500">Loading report data...</span>
                </div>
              </div>
            )}

            {/* Report Content */}
            {!loading && reportData && (
              <div className="space-y-4">
                {/* Export Header for Country Data */}
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-slate-800">Top & Bottom Sales by Country</h3>
                  <Button 
                    variant="outline" 
                    onClick={exportCountryData}
                    disabled={exportLoading}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    {exportLoading ? 'Exporting...' : 'Export to Excel'}
                  </Button>
                </div>
                
                <Tabs defaultValue="AU" value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="AU">Australia</TabsTrigger>
                    <TabsTrigger value="US">United States</TabsTrigger>
                    <TabsTrigger value="UK">United Kingdom</TabsTrigger>
                  </TabsList>
                
                {['AU', 'US', 'UK'].map(region => (
                  <TabsContent key={region} value={region} className="space-y-6">
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {reportData.top10[region] && reportData.top10[region].length > 0 && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg text-slate-800">Top 10 {region} Products Sales</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="h-[320px]">
                            <Bar 
                              data={generateTopMTDChartData(reportData.top10[region])} 
                              options={horizontalBarOptions} 
                            />
                          </div>
                        </CardContent>
                      </Card>
                      )}
                      {/* Bottom 10 Chart */}
                      {reportData.bottom10[region] && reportData.bottom10[region].length > 0 && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg text-slate-800">Bottom 10 {region} Products Sales</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="h-[320px]">
                            <Bar 
                              data={generateBottomChartData(reportData.bottom10[region])} 
                              options={horizontalBarOptions} 
                            />
                          </div>
                        </CardContent>
                      </Card>
                      )}
                    </div>
                      
                    {/* Top 10 Table */}
                    <TopBottomTable
                      data={reportData.top10[region]}
                      region={region}
                      title={`Top 10 ${region} Products`}
                      getStockToMonthLevelColor={getStockToMonthLevelColor}
                    />
                    
                    {/* Bottom 10 Table */}
                    <TopBottomTable
                      data={reportData.bottom10[region]}
                      region={region}
                      title={`Bottom 10 ${region} Products`}
                      getStockToMonthLevelColor={getStockToMonthLevelColor}
                    />
                    
                    {/* AU Channels Tables */}
                    {region === 'AU' && reportData.auChannels && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        {/* AU Online Top 10 */}
                        <div>
                          <h3 className="text-lg font-semibold mb-4">AU Online Top 10</h3>
                          <div className="overflow-x-auto overflow-y-auto max-h-[400px] border rounded-lg shadow-sm">
                            <Table className="w-full border">
                              <TableHeader className="bg-gray-100 sticky top-0 z-10">
                                <TableRow>
                                  <TableHead className="sticky top-0 bg-gray-100 border-b border-gray-200">SKU</TableHead>
                                  <TableHead className="sticky top-0 bg-gray-100 border-b border-gray-200">Product Name</TableHead>
                                  <TableHead className="sticky top-0 bg-gray-100 border-b border-gray-200">Color</TableHead>
                                  <TableHead className="text-right sticky top-0 bg-gray-100 border-b border-gray-200">QTY</TableHead>
                                  <TableHead className="text-right sticky top-0 bg-gray-100 border-b border-gray-200">SOH</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {reportData.auChannels.onlineTop10?.map((item, index) => (
                                  <TableRow key={`online-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <TableCell className="font-medium">{item.sku}</TableCell>
                                    <TableCell>{item.productName}</TableCell>
                                    <TableCell>{item.color}</TableCell>
                                    <TableCell className="text-right font-semibold">{item.mtd}</TableCell>
                                    {console.log("current item", item)}
                                    <TableCell className={`text-right ${getStockToMonthLevelColor(item.soh)}`}>
                                      {item.soh}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                        
                        {/* AU Retail Top 10 */}
                        <div>
                          <h3 className="text-lg font-semibold mb-4">AU Retail Top 10</h3>
                          <div className="overflow-x-auto overflow-y-auto max-h-[400px] border rounded-lg shadow-sm">
                            <Table className="w-full border">
                              <TableHeader className="bg-gray-100 sticky top-0 z-10">
                                <TableRow>
                                  <TableHead className="sticky top-0 bg-gray-100 border-b border-gray-200">SKU</TableHead>
                                  <TableHead className="sticky top-0 bg-gray-100 border-b border-gray-200">Product Name</TableHead>
                                  <TableHead className="sticky top-0 bg-gray-100 border-b border-gray-200">Color</TableHead>
                                  <TableHead className="text-right sticky top-0 bg-gray-100 border-b border-gray-200">QTY</TableHead>
                                  <TableHead className="text-right sticky top-0 bg-gray-100 border-b border-gray-200">SOH</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {reportData.auChannels.retailTop10?.map((item, index) => (
                                  <TableRow key={`retail-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <TableCell className="font-medium">{item.sku}</TableCell>
                                    <TableCell>{item.productName}</TableCell>
                                    <TableCell>{item.color}</TableCell>
                                    <TableCell className="text-right font-semibold">{item.mtd}</TableCell>
                                    <TableCell className={`text-right ${getStockToMonthLevelColor(item.soh)}`}>
                                      {item.soh}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      </div>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top & Bottom Sales by State Section */}
        <StateAnalysisSection 
          stateData={stateData}
          dateRange={dateRange}
          loading={loading}
          exportStateData={exportStateData}
          exportLoading={exportLoading}
        />
      </div>
      
     
    </div>
  );
};

export default TopBottomReport; 
